import React, { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, XIcon } from "@heroicons/react/outline";
import { Disclosure } from "@headlessui/react";
import { AddIcon } from "../../../assets/icons";
import { uploadAsset } from "../../../api/question.service";
import Loader from "../../shared/loader/loader";

const AuthorQuestionSettings = ({
  language,
  setSettingsDataEN,
  settingsDataEN,
  setSettingsDataES,
  settingsDataES,
  showImageGalleryPopup,
  setESTranslationDone,
  esTranslationDone,
}: {
  language: any;
  setSettingsDataEN: any;
  settingsDataEN: any;
  setSettingsDataES: any;
  settingsDataES: any;
  showImageGalleryPopup: any;
  setESTranslationDone: any;
  esTranslationDone: boolean;
}) => {
  const editorHeights = {
    INACTIVE_HEIGHT: "0px",
    TEXT_EDITOR_HEIGHT: "700px",
    VIDEO_EDITOR_HEIGHT: "340px",
    TTS_EDITOR_HEIGHT: "380px",
    NOTES_EDITOR_HEIGHT: "100px",
  };
  const [activeExplanation, setActiveExplanation] = useState(false);
  const [explanationHeight, setExplanationHeight] = useState(
    editorHeights.INACTIVE_HEIGHT
  );
  const [activeTTS, setActiveTTS] = useState(false);
  const [ttsHeight, setTTSHeight] = useState(editorHeights.INACTIVE_HEIGHT);
  const [activeNotes, setActiveNotes] = useState(false);
  const [notesHeight, setNotesHeight] = useState(editorHeights.INACTIVE_HEIGHT);
  const [SettingsInfo, setSettingsInfo] = useState<any>();
  const [explanationVideo, setExplanationVideo] = useState<string>("");
  const [explanationVtt, setExplanationVtt] = useState<string | null>(null);
  const [disableQuestionTTS, setDisableQuestionTTS] = useState<boolean>(false);
  const showExpText = false;
  const showExpVideo = true;
  const contentSpace = useRef(null);
  const [showVideoUploader, setShowVideoUploader] = useState<boolean>(false);
  const [showVttUploader, setShowVttUploader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (language === "EN") {
      setSettingsInfo(settingsDataEN);
    } else {
      setSettingsInfo(settingsDataES);
    }
    setDisableQuestionTTS(SettingsInfo?.disableQuestionTTS);
  }, [language, SettingsInfo?.disableQuestionTTS]);

  function toggleExplanation() {
    setActiveExplanation(!activeExplanation);
    if (showExpText) {
      setExplanationHeight(
        activeExplanation
          ? editorHeights.INACTIVE_HEIGHT
          : editorHeights.TEXT_EDITOR_HEIGHT
      );
    } else {
      setExplanationHeight(
        activeExplanation
          ? editorHeights.INACTIVE_HEIGHT
          : editorHeights.VIDEO_EDITOR_HEIGHT
      );
    }
    setActiveTTS(false);
    setTTSHeight(editorHeights.INACTIVE_HEIGHT);
    setActiveNotes(false);
    setNotesHeight(editorHeights.INACTIVE_HEIGHT);
  }

  useEffect(() => {
    setExplanationVideo(SettingsInfo?.videoExplanation);
    setExplanationVtt(SettingsInfo?.videoExplanationVttUrl);
  }, [SettingsInfo]);

  function toggleTSS() {
    setActiveTTS(activeTTS === false ? true : false);
    setTTSHeight(
      activeTTS
        ? editorHeights.INACTIVE_HEIGHT
        : editorHeights.TTS_EDITOR_HEIGHT
    );
    setActiveExplanation(false);
    setExplanationHeight(editorHeights.INACTIVE_HEIGHT);
    setActiveNotes(false);
    setNotesHeight(editorHeights.INACTIVE_HEIGHT);
  }

  function toggleNotes() {
    setActiveNotes(!activeNotes);
    setNotesHeight(
      activeNotes
        ? editorHeights.INACTIVE_HEIGHT
        : editorHeights.NOTES_EDITOR_HEIGHT
    );
    setActiveExplanation(false);
    setExplanationHeight(editorHeights.INACTIVE_HEIGHT);
    setActiveTTS(false);
    setTTSHeight(editorHeights.INACTIVE_HEIGHT);
  }

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  function onChangeExplanation(e) {
    const data = SettingsInfo;
    data.videoExplanation = e.target.value;

    setSettingsInfo(data);
    if (language == "EN") {
      setSettingsDataEN(data);
    } else {
      setSettingsDataES(data);
    }
  }

  function onChangeVtt(e) {
    const data = SettingsInfo;
    data.videoExplanationVttUrl = e.target.value;

    setSettingsInfo(data);
    if (language == "EN") {
      setSettingsDataEN(data);
    } else {
      setSettingsDataES(data);
    }
  }

  function onChangeDisableQuestionTTS(e) {
    const data = SettingsInfo;
    data.disableQuestionTTS = e.target.checked;
    setSettingsInfo(data);
    if (language == "EN") {
      setSettingsDataEN(data);
    } else {
      setSettingsDataES(data);
    }
  }
  function onChangeQuestionTTS(e) {
    const data = SettingsInfo;
    data.questionTTS = e.target.value;
    setSettingsInfo(data);
    if (language == "EN") {
      setSettingsDataEN(data);
    } else {
      setSettingsDataES(data);
    }
  }
  function onChangeNotes(e) {
    const data = SettingsInfo;
    data.notes = e.target.value;
    setSettingsInfo(data);
    if (language == "EN") {
      setSettingsDataEN(data);
    } else {
      setSettingsDataES(data);
    }
  }

  const onVideoUpload = async (e) => {
    setLoading(true);
    if (e.target.files[0]) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      formData.append("type", "videoExplanation");

      await uploadAsset(formData).then((res) => {
        if (res.data && res.data.success) {
          updateVideoUrl(res.data.data);
        }
        setLoading(false);
      });
    }
  };

  const onVttUpload = async (e) => {
    setLoading(true);
    if (e.target.files[0]) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      formData.append("type", "videoExplanationVtt");
      const suffix = language === "ES" ? "-es" : "";
      formData.append("suffix", suffix);

      await uploadAsset(formData).then((res) => {
        if (res.data && res.data.success) {
          updateVttUrl(res.data.data);
        }
        setLoading(false);
      });
    }
  };

  const updateVideoUrl = (videoUrl) => {
    //save same video url for EN and ES
    const dataEN = settingsDataEN;
    const dataES = settingsDataES;
    dataEN.videoExplanation = videoUrl;
    dataES.videoExplanation = videoUrl;
    if (language == "EN") {
      setSettingsInfo(dataEN);
    } else {
      setSettingsInfo(dataES);
    }
    setSettingsDataEN(dataEN);
    setSettingsDataES(dataES);

    setExplanationVideo(videoUrl);
    setShowVideoUploader(false);
  };

  const updateVttUrl = (vttUrl: string) => {
    // save different video url for EN and ES
    const dataEN = settingsDataEN;
    const dataES = settingsDataES;
    if (language == "EN") {
      dataEN.videoExplanationVttUrl = vttUrl;
      setSettingsInfo(dataEN);
      setSettingsDataEN(dataEN);
    } else {
      dataES.videoExplanationVttUrl = vttUrl;
      setSettingsInfo(dataES);
      setSettingsDataES(dataES);
    }

    setExplanationVtt(vttUrl);
    setShowVttUploader(false);
  };
  return (
    <>
      {loading && <Loader />}
      {showVideoUploader && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block w-full align-bottom bg-white px-4 pt-5 pb-8 text-left  shadow-xl transform transition-all sm:align-middle max-w-sm sm:p-6">
              <div className=" flex justify-end">
                <button
                  onClick={() => setShowVideoUploader(false)}
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                >
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="grid grid-cols-1 mt-5">
                <div className="text-sm flex items-center">
                  <label className="font-medium text-base text-gray-700">
                    Upload Video
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={onVideoUpload}
                    name="videoFile"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showVttUploader && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block w-full align-bottom bg-white px-4 pt-5 pb-8 text-left  shadow-xl transform transition-all sm:align-middle max-w-sm sm:p-6">
              <div className=" flex justify-end">
                <button
                  onClick={() => setShowVttUploader(false)}
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                >
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="grid grid-cols-1 mt-5">
                <div className="text-sm flex items-center">
                  <label className="font-medium text-base text-gray-700">
                    Upload Web VTT
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="file"
                    accept="text/vtt"
                    onChange={onVttUpload}
                    name="videoFile"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="bg-gray-50 shadow-lg">
        <div className="max-w-7xl mx-auto py-4 px-2 lg:px-2">
          <div className="max-w-3xl mx-auto">
            <h6 className="text-lg font-light text-gray-900 mb-2">Settings</h6>
            <div className="text-sm text-indigo-600 mb-4 mt-3">
              <a
                className="font-medium text-cyan-700 hover:underline cursor-pointer flex items-center"
                onClick={() => {
                  showImageGalleryPopup(true);
                }}
              >
                <svg
                  className="w-4 h-4 mr-2 fill-current text-gray-700"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path d="M 5 3 L 5 5 L 20 5 L 20 17 L 22 17 L 22 5 C 22 3.9069372 21.093063 3 20 3 L 5 3 z M 3 7 C 1.9069372 7 1 7.9069372 1 9 L 1 19 C 1 20.093063 1.9069372 21 3 21 L 16 21 C 17.093063 21 18 20.093063 18 19 L 18 9 C 18 7.9069372 17.093063 7 16 7 L 3 7 z M 3 9 L 16 9 L 16 19 L 3 19 L 3 9 z"></path>
                </svg>{" "}
                Image Gallery
              </a>
            </div>
            <dl className="space-y-6">
              <Disclosure
                as="div"
                className="py-3 px-3 shadow rounded-lg bg-white"
              >
                <dt className="text-lg">
                  <button
                    className="text-left w-full flex justify-between items-center text-gray-400"
                    onClick={() => toggleExplanation()}
                  >
                    <span className="text-gray-700 text-sm">Explanation</span>
                    <span className="flex items-center justify-center">
                      <ChevronDownIcon
                        className={classNames(
                          activeExplanation ? "-rotate-180" : "rotate-0",
                          "h-5 w-5 transform text-gray-700"
                        )}
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </dt>
                <div
                  ref={contentSpace}
                  style={{ maxHeight: explanationHeight }}
                  className="overflow-y-hidden transition-fit-content duration-700 ease-in-out"
                >
                  <div className="px-3 py-3 mt-3 border-t">
                    <div className="mt-2">
                      <label className="block mt-3">
                        <span className="inline-block text-gray-700 pb-2">
                          Video Explanation Link
                        </span>
                        <div className="flex items-center gap-x-2">
                          <input
                            className="appearance-none border border-gray-300 rounded-sm shadow-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            id="explanation"
                            type="text"
                            style={{ display: showExpVideo ? "block" : "none" }}
                            defaultValue={
                              explanationVideo ? explanationVideo : ""
                            }
                            onChange={(e) => onChangeExplanation(e)}
                          />
                          {showExpVideo && (
                            <span className="w-5 h-5 bg-[#1877b1] flex items-center justify-center">
                              <AddIcon
                                className="text-white"
                                onClick={() => setShowVideoUploader(true)}
                              ></AddIcon>
                            </span>
                          )}
                        </div>
                      </label>
                      <label className="block mt-3">
                        <span className="inline-block text-gray-700 pb-2">
                          VTT Captions Link
                        </span>
                        <div className="flex items-center gap-x-2">
                          <input
                            className="appearance-none border border-gray-300 rounded-sm shadow-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            id="explanation"
                            type="text"
                            style={{ display: showExpVideo ? "block" : "none" }}
                            defaultValue={explanationVtt ? explanationVtt : ""}
                            onChange={(e) => onChangeVtt(e)}
                          />
                          {showExpVideo && (
                            <span className="w-5 h-5 bg-[#1877b1] flex items-center justify-center">
                              <AddIcon
                                className="text-white"
                                onClick={() => setShowVttUploader(true)}
                              ></AddIcon>
                            </span>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </Disclosure>
            </dl>
            <dl className="space-y-6 mt-2">
              <Disclosure
                as="div"
                className="py-3 px-3 shadow rounded-lg bg-white"
              >
                <dt className="text-lg">
                  <button
                    className="text-left w-full flex justify-between items-center text-gray-400"
                    onClick={() => toggleTSS()}
                  >
                    <span className="text-gray-700 text-sm">TTS</span>
                    <span className="flex items-center justify-center">
                      <ChevronDownIcon
                        className={classNames(
                          activeTTS ? "-rotate-180" : "rotate-0",
                          "h-5 w-5 transform text-gray-700"
                        )}
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </dt>
                <div
                  ref={contentSpace}
                  style={{ maxHeight: ttsHeight }}
                  className="overflow-y-hidden transition-max-height duration-700 ease-in-out"
                >
                  <div className="px-3 py-3 mt-3 border-t">
                    <div className="mt-2">
                      <div className="mt-2">
                        <label className="flex items-center pb-2">
                          <input
                            type="checkbox"
                            className=" focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                            checked={disableQuestionTTS}
                            onChange={(e) => {
                              onChangeDisableQuestionTTS(e);
                              setDisableQuestionTTS(!disableQuestionTTS);
                            }}
                          />
                          <span className="ml-2 text-gray-700">
                            Disable Question TTS
                          </span>
                        </label>
                        <textarea
                          className="w-full shadow-sm border-gray-300 resize border rounded-md dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white"
                          onChange={(e) => onChangeQuestionTTS(e)}
                          defaultValue={SettingsInfo?.questionTTS}
                        ></textarea>
                        <div className={"grid grid-cols-12 gap-4"}>
                          <div className={"mt-3 col-span-9 ..."}>
                            <input
                              className="shadow-sm appearance-none border rounded-md border-gray-300 w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white focus:shadow-outline"
                              id="username"
                              type="text"
                            />
                          </div>
                          <div className={"mt-4 col-span-3"}>
                            <label className="rounded-lg p-2.5 shadow-lg tracking-wide border border-blue cursor-pointer bg-gray-200">
                              <span className="mt-2 text-base leading-normal">
                                Select
                              </span>
                              <input type="file" className="hidden" />
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Disclosure>
            </dl>
            <dl className="space-y-6 mt-2">
              <Disclosure
                as="div"
                className="py-3 px-3 shadow rounded-lg bg-white"
              >
                <dt className="text-lg">
                  <button
                    className="text-left w-full flex justify-between items-center text-gray-400"
                    onClick={() => toggleNotes()}
                  >
                    <span className="text-gray-700 text-sm">Notes</span>
                    <span className="flex items-center justify-center">
                      <ChevronDownIcon
                        className={classNames(
                          activeNotes ? "-rotate-180" : "rotate-0",
                          "h-5 w-5 transform text-gray-700"
                        )}
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </dt>
                <div
                  ref={contentSpace}
                  style={{
                    maxHeight: notesHeight,
                  }}
                  className="overflow-y-hidden transition-max-height duration-700 ease-in-out"
                >
                  <div className="px-3 py-3 mt-3 border-t">
                    <div className="mt-2">
                      <div className="mt-2">
                        <textarea
                          className="w-full resize border border-gray-300 shadow-sm rounded-md dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white"
                          onChange={(e) => onChangeNotes(e)}
                          defaultValue={SettingsInfo?.notes}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </Disclosure>
            </dl>

            <dl className="space-y-6 mt-4">
              <label
                htmlFor="toogleA"
                className="flex items-center cursor-pointer pl-1"
              >
                <div className="relative">
                  <input
                    id="toogleA"
                    type="checkbox"
                    className="sr-only"
                    onChange={(e) => setESTranslationDone(e.target.checked)}
                  />
                  <div className="w-10 h-4 bg-gray-400 rounded-full shadow-inner"></div>
                  <div
                    className={`${
                      esTranslationDone ? "bg-primary-color toggle-btn" : ""
                    } absolute w-6 h-6 bg-white rounded-full shadow -left-1 -top-1 transition`}
                  ></div>
                </div>
                <div className="ml-3 text-gray-700 font-medium">
                  Human Translated
                </div>
              </label>
            </dl>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthorQuestionSettings;
